import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  useTheme,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';

const MotionBox = motion(Box);

const industries = [
  { name: 'OIL & GAS', image: 'https://images.pexels.com/photos/247763/pexels-photo-247763.jpeg?auto=compress&cs=tinysrgb&w=400' },
  { name: 'CABLES', image: 'https://images.pexels.com/photos/257736/pexels-photo-257736.jpeg?auto=compress&cs=tinysrgb&w=400' },
  { name: 'REWINDING', image: 'https://images.pexels.com/photos/3785935/pexels-photo-3785935.jpeg?auto=compress&cs=tinysrgb&w=400' },
  { name: 'MILLING', image: 'https://images.pexels.com/photos/1108101/pexels-photo-1108101.jpeg?auto=compress&cs=tinysrgb&w=400' },
  { name: 'MINING', image: 'https://images.pexels.com/photos/2101137/pexels-photo-2101137.jpeg?auto=compress&cs=tinysrgb&w=400' },
  { name: 'CEMENT', image: 'https://images.pexels.com/photos/2219024/pexels-photo-2219024.jpeg?auto=compress&cs=tinysrgb&w=400' },
  { name: 'CERAMIC', image: 'https://images.pexels.com/photos/2062431/pexels-photo-2062431.jpeg?auto=compress&cs=tinysrgb&w=400' },
  { name: 'PAPER', image: 'https://images.pexels.com/photos/6169668/pexels-photo-6169668.jpeg?auto=compress&cs=tinysrgb&w=400' },
  { name: 'STEEL', image: 'https://images.pexels.com/photos/3846517/pexels-photo-3846517.jpeg?auto=compress&cs=tinysrgb&w=400' },
  { name: 'ENERGY', image: 'https://images.pexels.com/photos/356036/pexels-photo-356036.jpeg?auto=compress&cs=tinysrgb&w=400' },
  { name: 'FOOD', image: 'https://images.pexels.com/photos/3184183/pexels-photo-3184183.jpeg?auto=compress&cs=tinysrgb&w=400' },
  { name: 'AGRICULTURE', image: 'https://images.pexels.com/photos/2132171/pexels-photo-2132171.jpeg?auto=compress&cs=tinysrgb&w=400' },
];

const Industries = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      id="industries"
      sx={{
        bgcolor: isDark ? '#212121' : 'background.paper',
        position: 'relative',
        overflow: 'hidden',
        py: { xs: 8, md: 12 },
      }}
    >
      {/* Decorative Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: -100,
          left: -100,
          width: 600,
          height: 600,
          opacity: 0.05,
          background: 'radial-gradient(circle, #e57373 0%, transparent 70%)',
          display: { xs: 'none', md: 'block' },
        }}
      />
      
      <Container maxWidth="xl" sx={{ mb: 8, textAlign: 'center', position: 'relative', zIndex: 2 }}>
        <Typography
          variant="h2"
          sx={{
            color: isDark ? 'white' : 'text.primary',
            fontFamily: '"Orbitron", "Rajdhani", sans-serif',
            fontSize: { xs: '2rem', md: '3rem' },
            fontWeight: 700,
            letterSpacing: '0.1em',
            mb: 2,
          }}
        >
          INDUSTRIES <span style={{ color: '#e57373' }}>WE SERVE</span>
        </Typography>
        <Typography
          sx={{
            color: isDark ? 'rgba(255,255,255,0.7)' : 'text.secondary',
            fontSize: { xs: '1rem', md: '1.2rem' },
            maxWidth: 800,
            mx: 'auto',
            lineHeight: 1.8,
          }}
        >
          Supplying quality industrial spare parts, tools, and materials to more than 20 major sectors across Saudi Arabia.
        </Typography>
      </Container>

      {/* Industries Grid */}
      <Container maxWidth="xl">
        <Grid container>
        {industries.map((industry, index) => (
          <Grid item xs={6} sm={4} md={3} lg={2} key={index}>
            <MotionBox
              onHoverStart={() => setHoveredIndex(index)}
              onHoverEnd={() => setHoveredIndex(null)}
              sx={{
                height: { xs: 140, sm: 180, md: 220 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                cursor: 'pointer',
                borderRight: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
                borderBottom: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
                overflow: 'hidden',
                bgcolor: isDark ? '#2c2c2c' : '#ffffff',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: isDark ? '#333' : '#f5f5f5',
                  zIndex: 10,
                }
              }}
            >
              {/* Background Image on Hover */}
              <AnimatePresence>
                {hoveredIndex === index && (
                  <MotionBox
                    initial={{ opacity: 0, scale: 1.2 }}
                    animate={{ opacity: 0.4, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.2 }}
                    transition={{ duration: 0.4 }}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      backgroundImage: `url(${industry.image})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      zIndex: 1,
                    }}
                  />
                )}
              </AnimatePresence>

              {/* Content */}
              <Box 
                sx={{ 
                  zIndex: 2, 
                  textAlign: 'center', 
                  width: '100%',
                  px: 2,
                }}
              >
                <Typography
                  sx={{
                    color: isDark ? 'white' : 'text.primary',
                    fontSize: { xs: '0.9rem', md: '1.1rem' },
                    fontWeight: 600,
                    letterSpacing: 1,
                    fontFamily: '"Rajdhani", sans-serif',
                    transition: 'all 0.3s ease',
                    transform: hoveredIndex === index ? 'scale(1.1)' : 'scale(1)',
                    textShadow: hoveredIndex === index ? '0 0 10px rgba(229, 115, 115, 0.5)' : 'none',
                  }}
                >
                  {industry.name}
                </Typography>
                
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: hoveredIndex === index ? '100%' : 0 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    height: 2,
                    backgroundColor: '#e57373',
                    marginTop: 8,
                    marginInline: 'auto',
                    maxWidth: 40,
                  }}
                />
              </Box>
            </MotionBox>
          </Grid>
        ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Industries;
